import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/site/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const SECTIONS = [
  { k: "all", l: "All Sarees" },
  { k: "fresh", l: "Fresh Arrivals" },
  { k: "clearance", l: "Clearance Sale" },
  { k: "out", l: "Out of Stock" },
  { k: "other", l: "Other" },
];

export default function Store() {
  const [sp, setSp] = useSearchParams();
  const section = sp.get("section") || "all";
  const origin = sp.get("origin") || "";
  const sort = sp.get("sort") || "newest";
  const [items, setItems] = useState(null);
  const [origins, setOrigins] = useState([]);

  const set = (k, v) => {
    const n = new URLSearchParams(sp);
    v ? n.set(k, v) : n.delete(k);
    setSp(n);
  };

  useEffect(() => {
    api.get("/origins").then((r) => setOrigins(r.data));
  }, []);

  useEffect(() => {
    setItems(null);
    api.get("/products", { params: { section, origin: section === "other" ? "" : origin, sort } }).then((r) => setItems(r.data));
  }, [section, origin, sort]);

  return (
    <div className="min-h-screen" data-testid="store-page">
      <Header />
      <section className="relative pt-36 pb-12 px-6 mx-auto max-w-6xl">
        <p className="text-[11px] uppercase tracking-[0.5em] text-[#D4AF37]">The store</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4">Sarees that <span className="font-serif-it text-[#E06D3B]">speak</span></h1>
        <p className="text-[#A89F91] mt-4 max-w-xl">Handwoven silk from every corner of India. Choose by region, by season of the sale, or by what just left the loom.</p>
      </section>

      <div className="sticky top-20 z-40 bg-[#0B0907]/85 backdrop-blur-xl border-y border-[#D4AF37]/10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div className="flex gap-2 overflow-x-auto scrollbar-thin">
            {SECTIONS.map((s) => (
              <button key={s.k} onClick={() => set("section", s.k === "all" ? "" : s.k)} className={`shrink-0 rounded-full px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${section === s.k ? "bg-[#D4AF37] text-[#0B0907] border-[#D4AF37]" : "border-[#D4AF37]/25 text-[#A89F91] hover:text-[#F3E5AB]"}`} data-testid={`filter-${s.k}`}>
                {s.l}
              </button>
            ))}
          </div>
          <Select value={sort} onValueChange={(v) => set("sort", v === "newest" ? "" : v)}>
            <SelectTrigger className="w-full lg:w-56 rounded-full bg-[#14110E] border-[#D4AF37]/25" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#14110E] border-[#D4AF37]/25 text-[#F5EFE6]">
              <SelectItem value="newest" data-testid="sort-newest">Newest first</SelectItem>
              <SelectItem value="price_desc" data-testid="sort-price-desc">Price: High to Low</SelectItem>
              <SelectItem value="price_asc" data-testid="sort-price-asc">Price: Low to High</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {section !== "other" && origins.length > 0 && (
          <div className="mx-auto max-w-6xl px-6 pb-4 flex gap-2 overflow-x-auto scrollbar-thin">
            <button onClick={() => set("origin", "")} className={`shrink-0 font-serif-it text-base px-3 ${!origin ? "text-[#F3E5AB] underline underline-offset-4" : "text-[#6E6558] hover:text-[#A89F91]"}`} data-testid="origin-filter-all">All regions</button>
            {origins.map((o) => (
              <button key={o} onClick={() => set("origin", o)} className={`shrink-0 font-serif-it text-base px-3 ${origin === o ? "text-[#F3E5AB] underline underline-offset-4" : "text-[#6E6558] hover:text-[#A89F91]"}`} data-testid={`origin-filter-${o.toLowerCase()}`}>
                {o}
              </button>
            ))}
          </div>
        )}
      </div>

      <section className="mx-auto max-w-6xl px-6 py-12">
        {items === null && <p className="text-[#6E6558]">Unfolding the collection…</p>}
        {items?.length === 0 && <p className="font-serif-it text-2xl text-[#A89F91]" data-testid="store-empty">Nothing on these looms yet. Try another section.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10" data-testid="product-grid">
          {items?.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>
      <Footer />
    </div>
  );
}
